// Unified Analytics Service
// Service that sends events to Mixpanel and Google Analytics 4

import mixpanelService from './mixpanelService';
import ga4Service from './ga4Service';
import { createFileLogger } from '../../utils/logger';
import { isLocalhost } from '../../config/mixpanel';

export interface UnifiedAnalyticsEvent {
  name: string;
  category?: string;
  properties?: Record<string, any>;
  value?: number;
}

export interface UnifiedPageView {
  page_title?: string;
  page_path?: string;
  properties?: Record<string, any>;
}

export interface UnifiedUserProperties {
  user_id?: string;
  properties?: Record<string, any>;
}

class UnifiedAnalyticsService {
  private log = createFileLogger('ANALYTICS_SERVICE');
  
  /**
   * Check if analytics should be disabled (localhost)
   */
  private isAnalyticsDisabled(): boolean {
    return isLocalhost();
  }
  
  /**
   * Track an event in Mixpanel and Google Analytics 4
   * @param event Event data to track
   */
  track(event: UnifiedAnalyticsEvent): void {
    // Block all tracking on localhost
    if (this.isAnalyticsDisabled()) {
      this.log.debug('[LOCALHOST] Analytics disabled, skipping event:', event.name);
      return;
    }
    
    // Send to Mixpanel
    try {
      mixpanelService.track(event.name, event.properties);
    } catch (error) {
      this.log.warn('Failed to send event to Mixpanel:', error);
    }

    // Send to GA4
    try {
      ga4Service.track(event.name, event.properties);
    } catch (error) {
      this.log.warn('Failed to send event to GA4:', error);
    }
  }

  /**
   * Track a page view in Mixpanel and Google Analytics 4
   * @param pageView Page view data
   */
  trackPageView(pageView: UnifiedPageView): void {
    // Block all tracking on localhost
    if (this.isAnalyticsDisabled()) {
      this.log.debug('[LOCALHOST] Analytics disabled, skipping page view:', pageView.page_path);
      return;
    }
    
    // Send to Mixpanel
    try {
      mixpanelService.track('Page View', {
        page_title: pageView.page_title,
        page_path: pageView.page_path,
        ...pageView.properties,
      });
    } catch (error) {
      this.log.warn('Failed to send page view to Mixpanel:', error);
    }

    // Send to GA4
    try {
      ga4Service.trackPageView(pageView.page_title, pageView.page_path);
    } catch (error) {
      this.log.warn('Failed to send page view to GA4:', error);
    }
  }

  /**
   * Set user properties in Mixpanel and Google Analytics 4
   * @param userProperties User properties to set
   */
  setUserProperties(userProperties: UnifiedUserProperties): void {
    // Block all tracking on localhost
    if (this.isAnalyticsDisabled()) {
      this.log.debug('[LOCALHOST] Analytics disabled, skipping user properties');
      return;
    }
    
    // Send to Mixpanel
    try {
      if (userProperties.user_id) {
        mixpanelService.identify(userProperties.user_id);
      }
      if (userProperties.properties) {
        mixpanelService.setUserProperties(userProperties.properties);
      }
    } catch (error) {
      this.log.warn('Failed to set user properties in Mixpanel:', error);
    }

    // Send to GA4
    try {
      if (userProperties.user_id) {
        ga4Service.identify(userProperties.user_id);
      }
      if (userProperties.properties) {
        ga4Service.setUserProperties(userProperties.properties);
      }
    } catch (error) {
      this.log.warn('Failed to set user properties in GA4:', error);
    }
  }

  /**
   * Track user login in both services
   * @param method Login method
   * @param userId User ID
   */
  trackLogin(method: string, userId?: string): void {
    if (this.isAnalyticsDisabled()) {
      this.log.debug('[LOCALHOST] Analytics disabled, skipping login tracking');
      return;
    }
    
    this.track({
      name: 'login',
      category: 'engagement',
      properties: {
        method,
        user_id: userId,
      }
    });
  }

  /**
   * Track user signup in both services
   * @param method Signup method
   * @param userId User ID
   * @param additionalProperties Additional properties to track
   */
  trackSignup(method: string, userId?: string, additionalProperties?: Record<string, any>): void {
    if (this.isAnalyticsDisabled()) {
      this.log.debug('[LOCALHOST] Analytics disabled, skipping signup tracking');
      return;
    }
    
    this.track({
      name: 'signup_completed',
      category: 'conversion',
      properties: {
        method,
        user_id: userId,
        ...additionalProperties
      }
    });
  }

  /**
   * Track app usage in both services
   * @param appName Name of the app
   * @param action Action performed
   * @param additionalData Additional data
   */
  trackAppUsage(appName: string, action: string, additionalData?: Record<string, any>): void {
    if (this.isAnalyticsDisabled()) {
      this.log.debug('[LOCALHOST] Analytics disabled, skipping app usage tracking');
      return;
    }
    
    this.track({
      name: `app_${action}`,
      category: 'app_usage',
      properties: {
        app_name: appName,
        action,
        ...additionalData,
      }
    });
  }

  /**
   * Track search in both services - DISABLED
   * @param searchTerm Search term
   * @param searchType Type of search
   * @param resultCount Number of results
   */
  trackSearch(searchTerm: string, searchType: string, resultCount?: number): void {
    // Search tracking disabled - no Mixpanel event sent
    const log = createFileLogger('ANALYTICS_SERVICE');
    log.debug('Search tracking disabled:', { searchTerm: '[REDACTED]', searchType, resultCount });
  }

  /**
   * Track errors in both services
   * @param errorMessage Error message
   * @param errorType Type of error
   * @param additionalData Additional data
   */
  trackError(errorMessage: string, errorType: string, additionalData?: Record<string, any>): void {
    if (this.isAnalyticsDisabled()) {
      this.log.debug('[LOCALHOST] Analytics disabled, skipping error tracking');
      return;
    }
    
    this.track({
      name: 'error',
      category: 'error',
      properties: {
        error_message: errorMessage,
        error_type: errorType,
        ...additionalData,
      }
    });
  }

  /**
   * Track feature usage - DISABLED
   * @param featureName Name of the feature
   * @param action Action performed
   * @param additionalData Additional data
   */
  trackFeatureUsage(featureName: string, action: string, additionalData?: Record<string, any>): void {
    // Feature usage tracking disabled - no Mixpanel event sent
    const log = createFileLogger('ANALYTICS_SERVICE');
    log.debug('Feature usage tracking disabled:', { featureName, action });
  }

  /**
   * Track business events (conversions, purchases, etc.) - DISABLED
   * @param eventName Event name
   * @param value Value of the event
   * @param additionalData Additional data
   */
  trackBusinessEvent(eventName: string, value?: number, additionalData?: Record<string, any>): void {
    // Business event tracking disabled - no Mixpanel event sent
    const log = createFileLogger('ANALYTICS_SERVICE');
    log.debug('Business event tracking disabled:', { eventName, value });
  }

  /**
   * Check if analytics services are ready
   */
  isReady(): { mixpanel: boolean; ga4: boolean } {
    return {
      mixpanel: true, // Mixpanel service doesn't expose isReady, assume it's ready after init
      ga4: ga4Service.getInitializationStatus(),
    };
  }

  /**
   * Flush pending events before navigation
   * Prevents event loss when user navigates away immediately after an action
   */
  async flushEvents(): Promise<void> {
    if (this.isAnalyticsDisabled()) {
      this.log.debug('[LOCALHOST] Analytics disabled, skipping flush');
      return;
    }
    
    await Promise.all([
      mixpanelService.flushEvents(),
      ga4Service.flushEvents()
    ]);
  }

  /**
   * Track a funnel event
   * Convenience method for tracking signup funnel steps
   */
  trackFunnelStep(stepName: string, properties?: Record<string, any>): void {
    this.track({
      name: stepName,
      category: 'signup_funnel',
      properties
    });
  }
}

// Export singleton instance
const analyticsService = new UnifiedAnalyticsService();
export default analyticsService; 